/* ============================================================
   AGILITE QR RECEIPT CAPTURE  (Step 2) — ISOLATED asset
   ------------------------------------------------------------
   Photo capture + upload UI for the free-patch receipt step.
   Own file (isolation principle); reads window globals only.

   Device-adaptive:
     - MOBILE  (pointer: coarse): "Snap your receipt" opens the CAMERA
       directly via <input type=file capture="environment"> + a gallery
       upload button. No getUserMedia, no permission prompt.
     - DESKTOP: no camera button (laptop webcams are useless for paper
       receipts); instead a drag-&-drop zone + file picker. The `capture`
       attribute is simply ignored by desktop browsers, so nothing breaks.

   Pipeline per image: pick → client-side compress (canvas, max 1600px,
   JPEG ~0.82 — a 4-8MB phone photo becomes ~200-500KB, cheaper to upload
   AND cheaper in vision-model tokens later) → preview for the shopper →
   POST multipart FormData to window.__AGB_RECEIPT_ENDPOINT.

   THE REWARD GATE (Dorian's rule 2026-08-17): the receipt verdict decides
   the prize. POST -> {receipt_id} -> poll <endpoint>/<id>/verdict until
   pass/fail — only a PASS unlocks the patch; fail/error offer another try.
   No skip, no "reserved either way".

   Gating: window.__AGB_RECEIPT_ENDPOINT
     - unset/null  -> the whole step is DISABLED (qr-signup.js hides the
                      CTA) so the live page never promises an unbuilt step.
     - '__console__' -> dev stub: full UI; verdict is
                      window.__AGB_RECEIPT_STUB_VERDICT || 'pass'.
     - URL          -> the receipt-intel public storefront door.
   ============================================================ */
(function () {
  'use strict';

  var ENDPOINT = window.__AGB_RECEIPT_ENDPOINT || null;
  var MAX_DIM = 1600;      // longest side after downscale
  var JPEG_Q  = 0.82;
  var MAX_UPLOAD_BYTES = 10 * 1024 * 1024; // reject absurd files before compressing

  var COARSE = false;
  try { COARSE = window.matchMedia && window.matchMedia('(pointer: coarse)').matches; } catch (e) {}

  var _signup = null;      // the signup payload handed over by qr-signup.js
  var _blob = null;        // the compressed image awaiting send
  var _lastSource = null;  // 'cam' | 'lib' | 'drop' — Retake reopens the camera only for camera shots

  // ---- camera-trip resume ----------------------------------------------
  // On memory-pressed phones (hit on Android 2026-08-17) the OS can kill the
  // tab while the camera app is open; the browser then RELOADS the page on
  // return and the shopper would land back at the signup form. Persist the
  // step in sessionStorage (per-tab, dies with it) and restore on load.
  var RESUME_KEY = 'agbQrResume';
  var RESUME_TTL_MS = 30 * 60 * 1000;
  function saveResume() {
    try { sessionStorage.setItem(RESUME_KEY, JSON.stringify({ p: _signup, t: Date.now() })); } catch (e) {}
  }
  function clearResume() {
    try { sessionStorage.removeItem(RESUME_KEY); } catch (e) {}
  }
  function tryResume() {
    var saved = null;
    try { saved = JSON.parse(sessionStorage.getItem(RESUME_KEY) || 'null'); } catch (e) {}
    if (!saved || !saved.p || Date.now() - (saved.t || 0) > RESUME_TTL_MS) {
      if (saved) clearResume();
      return;
    }
    ['product', 'signup-form', 'headline', 'sub'].forEach(function (id) {
      var el = document.getElementById(id); if (el) el.hidden = true;
    });
    show(saved.p);
    // We only get here after a mid-step reload — usually the OS reclaiming the
    // tab during a camera trip. Point at the path that cannot fail that way.
    var hint = document.getElementById('rc-hint');
    if (hint) hint.hidden = false;
  }

  function enabled() { return !!ENDPOINT; }

  // ---- show/hide -----------------------------------------------------
  function show(signupPayload) {
    _signup = signupPayload || null;
    var step = document.getElementById('receipt-step');
    if (!step) return;
    saveResume();
    // NOTE: deliberately NOT prefetching OpenCV here — holding its WASM heap
    // while the OS camera is open caused "low memory" kills on iOS. qr-crop.js
    // loads it only after a photo exists, in parallel with the decode.
    var succ = document.getElementById('success');
    if (succ) succ.hidden = true;
    step.hidden = false;

    // device-adaptive controls
    var snap = document.getElementById('rc-snap');
    var drop = document.getElementById('rc-drop');
    if (snap) snap.hidden = !COARSE;   // camera button only where a camera makes sense
    if (drop) drop.hidden = COARSE;    // drag-&-drop only on desktop

    initForward();
    startGrantWatch();
  }

  // ---- forward-by-email (the brief's third option) ---------------------
  // "Have your receipt in your email? Forward it to <address>." The mail is
  // read by the receipt-intel email door; nothing is submitted from this page,
  // so the only way the page learns about it is by watching for the patch.
  function initForward() {
    var box = document.getElementById('rc-forward');
    if (!box) return;
    var addr = window.__AGB_FORWARD_EMAIL || null;
    if (!addr) { box.hidden = true; return; }
    var slot = document.getElementById('rc-fwd-addr');
    if (slot) slot.textContent = addr;
    box.hidden = false;

    // Ask for an address unique to THIS shopper. Without it we would have to
    // guess who forwarded the mail from its sender - and people forward from a
    // different mailbox than the one they typed here. Whoever mails the token
    // address IS this shopper, whatever they send from. Falls back to the
    // generic address if the mint fails: a generic forward still arrives, it
    // just gets matched by sender instead.
    if (ENDPOINT && ENDPOINT !== '__console__' && _signup && _signup.email && !box._token) {
      box._token = true;
      var base = ENDPOINT.replace(/\/receipts\/?$/, '');
      fetch(base + '/forward-token', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: _signup.email, vid: _signup.visitorId,
                               sku: _signup.sku, sessionId: _signup.sessionId,
                               region: _signup.region })
      })
        .then(function (r) { return r && r.ok ? r.json() : null; })
        .then(function (d) {
          if (d && d.address) {
            addr = d.address;
            if (slot) slot.textContent = addr;
            _fwdToken = d.token || null;
          }
        })
        .catch(function () { /* keep the generic address */ });
    }
    var btn = document.getElementById('rc-fwd-copy');
    if (btn && !btn._wired) {
      btn._wired = true;
      btn.addEventListener('click', function () {
        var done = function () {
          var was = btn.textContent;
          btn.textContent = 'Copied';
          window.setTimeout(function () { btn.textContent = was; }, 1400);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(addr).then(done, done);
        } else {
          var t = document.createElement('textarea');
          t.value = addr; document.body.appendChild(t); t.select();
          try { document.execCommand('copy'); } catch (e) {}
          t.remove(); done();
        }
      });
    }
  }

  // ---- watch for a patch granted OUTSIDE this page ----------------------
  // Covers both spec behaviours: a forwarded email arriving while they wait,
  // and a receipt approved by hand after they left and came back. Asks the
  // identity service "does this person hold a patch yet?" — it never creates
  // one, so polling is safe. Backs off, and stops after ~15 min.
  var GRANT_FIRST_MS = 8000, GRANT_MS = 12000, GRANT_MAX_MS = 900000;
  var _grantTimer = null, _grantT0 = 0, _grantStopped = false;
  var _fwdToken = null;

  // Why their emailed receipt didn't validate. Each reason has a different
  // fix, so a single "it failed" would leave them stuck.
  var FWD_REASON = {
    link_only: 'That email only had a <strong>link</strong> to your receipt, and we ' +
               'can&rsquo;t open links. Send the receipt itself &mdash; a screenshot, ' +
               'the PDF, or a photo.',
    unreadable: 'We couldn&rsquo;t read that receipt. Try a clearer photo, or attach ' +
                'the original PDF.',
    no_items: 'We couldn&rsquo;t find any items on that receipt. Make sure the whole ' +
              'receipt is visible, including the list of products.',
    brand_only: 'We can see Agilite on that receipt, but we couldn&rsquo;t make out ' +
                '<strong>which product</strong> — some shops print their own short ' +
                'names. Try a sharper photo of the item lines, or send the order ' +
                'confirmation instead.',
    wrong_product: 'We found Agilite items on that receipt, but not the one your tag ' +
                   'was scanned from. Send the receipt for <strong>that</strong> item.',
    already_used: 'That receipt has already been used to claim a patch. Each ' +
                  'receipt counts once &mdash; send a different one.',
    no_agilite: 'We couldn&rsquo;t find any Agilite products on that receipt. Send the ' +
                'receipt for the Agilite gear you bought.'
  };

  // The page never learns a receipt_id for an emailed receipt — it's created
  // server-side when the mail lands. The forwarding token is the handle.
  function checkForwardStatus() {
    if (!_fwdToken || !ENDPOINT || ENDPOINT === '__console__') return;
    var base = ENDPOINT.replace(/\/receipts\/?$/, '');
    fetch(base + '/forward-status/' + encodeURIComponent(_fwdToken))
      .then(function (r) { return r && r.ok ? r.json() : null; })
      .then(function (d) {
        if (!d || d.state !== 'received') return;
        if (d.verdict === 'pending') return;
        stopGrantWatch();
        // A PASS carries the claim link — act on it here. Waiting for the
        // identity poll to notice instead was the reason an emailed receipt
        // verified and the page never moved.
        if (d.verdict === 'pass') {
          if (d.claim_url) grantArrived(d);
          return;
        }
        showForwardProblem(d.reason);
      })
      .catch(function () {});
  }

  function showForwardProblem(reason) {
    var note = document.getElementById('rc-fwd-note');
    if (!note) return;
    note.classList.remove('waiting');
    note.classList.add('err');
    note.innerHTML = '<strong>We got your email, but it didn&rsquo;t go through.</strong><br>' +
      (FWD_REASON[reason] || FWD_REASON.unreadable) +
      '<br>You can also snap or upload it above.';
  }

  // TWO independent watches, because they answer different questions and one
  // must not wait on the other:
  //   forward-status  — "did the receipt I emailed land?" This is also what
  //                     GRANTS the patch, so it runs FAST. Measured: the
  //                     backend settles ~4s after the mail arrives, so a flat
  //                     12s poll behind another request made the page look
  //                     stuck for ~13s after the receipt already said Validated.
  //   customer-check  — "was I granted a patch some other way?" (approved by
  //                     hand, verified earlier). Rare; slow cadence is fine.
  var FWD_TIERS = [[60000, 3000], [180000, 8000]];   // <1min: 3s, <3min: 8s
  var FWD_SLOW_MS = 20000;
  var _fwdTimer = null;

  function fwdDelay() {
    var age = Date.now() - _grantT0;
    for (var i = 0; i < FWD_TIERS.length; i++) {
      if (age < FWD_TIERS[i][0]) return FWD_TIERS[i][1];
    }
    return FWD_SLOW_MS;
  }

  function startGrantWatch() {
    if (_grantStopped) return;
    if (!_signup || !_signup.email) return;
    if (typeof fetch !== 'function') return;
    _grantT0 = Date.now();
    if (!_fwdTimer) _fwdTimer = window.setTimeout(fwdTick, 2500);
    if (!_grantTimer && window.AGB_VID_EMAIL_URL) {
      _grantTimer = window.setTimeout(grantTick, GRANT_FIRST_MS);
    }
  }

  function fwdTick() {
    _fwdTimer = null;
    if (_grantStopped) return;
    if (Date.now() - _grantT0 > GRANT_MAX_MS) return;
    checkForwardStatus();
    if (!_grantStopped) _fwdTimer = window.setTimeout(fwdTick, fwdDelay());
  }

  function stopGrantWatch() {
    _grantStopped = true;
    if (_grantTimer) { window.clearTimeout(_grantTimer); _grantTimer = null; }
    if (_fwdTimer) { window.clearTimeout(_fwdTimer); _fwdTimer = null; }
  }

  function grantTick() {
    _grantTimer = null;
    if (_grantStopped) return;
    if (Date.now() - _grantT0 > GRANT_MAX_MS) return;
    var base = String(window.AGB_VID_EMAIL_URL).replace(/\/+$/, '');
    fetch(base + '/customer-check', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: _signup.email, vid: _signup.visitorId,
                             sku: _signup.sku })
    })
      .then(function (r) { return r && r.ok ? r.json() : null; })
      .then(function (d) {
        if (d && d.claim_url && (d.already_granted || d.claim === 'already'
                                 || d.existing_customer)) {
          stopGrantWatch();
          grantArrived(d);
          return;
        }
        // forward-status has its own faster timer — don't chain it here.
        _grantTimer = window.setTimeout(grantTick, GRANT_MS);
      })
      .catch(function () { _grantTimer = window.setTimeout(grantTick, GRANT_MS); });
  }

  function grantArrived(d) {
    clearResume();
    var step = document.getElementById('receipt-step');
    var succ = document.getElementById('success');
    if (step) step.hidden = true;
    if (succ) {
      succ.hidden = false;
      succ.innerHTML =
        '<div class="ok-badge">&#10003; Purchase verified</div>' +
        '<h2 class="ag-heading">Your Patch Is Unlocked</h2>' +
        '<p class="sub">We received your receipt. Taking you to claim <strong>' +
        esc(d.patch_title || 'your free patch') + '</strong>&hellip;</p>' +
        '<a class="ag-btn block" href="' + esc(d.claim_url) + '">Claim your free patch &rarr;</a>';
    }
    // Per the brief: once it arrives, they are redirected to claim it.
    try { window.top.location.href = d.claim_url; }
    catch (e) { window.location.href = d.claim_url; }
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // ---- image handling --------------------------------------------------
  function decode(file) {
    // createImageBitmap honors EXIF orientation in modern browsers.
    if (window.createImageBitmap) {
      try {
        return window.createImageBitmap(file, { imageOrientation: 'from-image' })
          .catch(function () { return window.createImageBitmap(file); });
      } catch (e) { /* fall through */ }
    }
    return new Promise(function (resolve, reject) {
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () { URL.revokeObjectURL(url); resolve(img); };
      img.onerror = function () { URL.revokeObjectURL(url); reject(new Error('decode_failed')); };
      img.src = url;
    });
  }

  function compress(file) {
    return decode(file).then(function (img) {
      var w = img.width || img.naturalWidth, h = img.height || img.naturalHeight;
      var scale = Math.min(1, MAX_DIM / Math.max(w, h));
      var cw = Math.max(1, Math.round(w * scale)), ch = Math.max(1, Math.round(h * scale));
      var canvas = document.createElement('canvas');
      canvas.width = cw; canvas.height = ch;
      canvas.getContext('2d').drawImage(img, 0, 0, cw, ch);
      if (img.close) { try { img.close(); } catch (e) {} }
      return new Promise(function (resolve, reject) {
        canvas.toBlob(function (b) {
          if (!b) return reject(new Error('encode_failed'));
          // if "compression" somehow grew a tiny file, keep the original
          resolve(b.size < file.size ? b : file);
        }, 'image/jpeg', JPEG_Q);
      });
    });
  }

  function onFile(file) {
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) { setStatus('That image is too large — try again.', true); return; }
    if (!/^image\//.test(file.type))  { setStatus('Please choose a photo.', true); return; }
    setStatus('Preparing photo…', false);
    // Auto-crop first when qr-crop.js is present — best-effort: any failure
    // resolves to the untouched original and the flow continues unchanged.
    var pre = (window.AGBQrCrop && window.AGBQrCrop.enabled())
      ? window.AGBQrCrop.process(file, function (stage) {
          setStatus(stage === 'loading-cv' ? 'Preparing the scanner…' : 'Finding your receipt…', false);
        })
      : Promise.resolve({ blob: file, cropped: false });
    pre.then(function (res) {
      // CAPTURE→CROP (Dorian 2026-08-19): a fresh photo lands straight on
      // the corner-crop screen with exactly two choices — Submit (warp +
      // send immediately) or Retake photo. No intermediate preview.
      if (window.AGBQrCrop && window.AGBQrCrop.enabled() &&
          window.AGBQrCrop.openCapture({
            container: document.getElementById('receipt-step'),
            onSubmit: function (blob) {
              compress(blob).then(function (b) { _blob = b; send(); });
            },
            onRetake: function () {
              _blob = null;
              toggle('rc-preview', false);
              toggle('rc-actions', true); toggle('rc-drop', true);
              var cam = document.getElementById('rc-file-cam');
              if (_lastSource === 'cam' && cam) {
                try { cam.value = ''; cam.click(); } catch (e) {}
              }
            }
          })) {
        setStatus('', false);
        toggle('rc-actions', false); toggle('rc-drop', false);
        return;
      }
      // No crop session (CV unavailable / detection failed) — classic preview.
      return compress(res.blob).then(function (blob) {
        setPreview(blob, res.cropped ? 'auto-cropped to the receipt' : null);
      });
    }).catch(function () {
      setStatus('Could not read that photo — try another.', true);
    });
  }

  function setPreview(blob, note) {
    _blob = blob;
    var img = document.getElementById('rc-img');
    if (img) {
      if (img._url) URL.revokeObjectURL(img._url);
      img._url = URL.createObjectURL(blob);
      img.src = img._url;
    }
    var meta = document.getElementById('rc-meta');
    if (meta) meta.textContent = Math.round(blob.size / 1024) + ' KB ready' +
      (note ? ' · ' + note : '');
    toggle('rc-actions', false); toggle('rc-drop', false); toggle('rc-preview', true);
    setStatus('', false);
    // Crop controls (Adjust / Use full photo) under the preview, when available.
    if (window.AGBQrCrop && window.AGBQrCrop.enabled()) {
      window.AGBQrCrop.offerControls({
        container: document.getElementById('rc-preview'),
        onBlob: function (newBlob, label) {
          compress(newBlob).then(function (b) {
            setPreview(b, label === 'cropped' ? 'cropped to the receipt' : null);
          });
        }
      });
    }
  }

  // ---- verification loading screen: scorpion in a spinning ring --------
  // The Agilite scorpion (line-art from Isaiah's ScorpionLoader, static body
  // + gentle tail sway) centered in a circular oxblood spinner. Shown from
  // Submit until the verdict settles. DOM + styles are self-injected so the
  // page markup stays untouched.
  var SCORPION_SVG =
    '<svg aria-hidden width="72" height="54" viewBox="0 0 64 48" fill="none" ' +
    'stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" style="overflow:visible">' +
    '<path d="M18 35 L15 39 L12 43" stroke-width="1.3" opacity="0.85"/>' +
    '<path d="M23 34.5 L21 39 L19 43.5" stroke-width="1.3" opacity="0.85"/>' +
    '<path d="M28 34 L27.5 39 L27 43.5" stroke-width="1.3" opacity="0.85"/>' +
    '<path d="M33 34.5 L34.5 39 L36.5 43.5" stroke-width="1.3" opacity="0.85"/>' +
    '<path d="M12 37 C16 31.5 24 30.5 30 32 C34 33 38.5 34 42 34.7" stroke-width="2"/>' +
    '<path d="M20 31.9 L20.5 34.6" stroke-width="1" opacity="0.7"/>' +
    '<path d="M26 31.1 L26.3 34.1" stroke-width="1" opacity="0.7"/>' +
    '<path d="M32 32.4 L32.2 34.6" stroke-width="1" opacity="0.7"/>' +
    '<path d="M42 34.7 Q44.5 35 46 35.6" stroke-width="2"/>' +
    '<path d="M45.5 35.5 L49 37" stroke-width="1.5"/>' +
    '<path d="M49 37 Q54 37.6 57.5 36.4" stroke-width="1.5"/>' +
    '<path d="M49 37 Q53 34.3 57.5 35.2" stroke-width="1.5"/>' +
    '<g class="rc-scorp-tail">' +
    '<path d="M12 37 C8 33 6 27.5 8.2 22.5 C9.8 18.8 13.5 16.2 17.5 16 C20.5 15.9 23.3 17.3 25.2 19.6" stroke-width="1.8"/>' +
    '<path d="M9.4 28.5 L12 29.3" stroke-width="1" opacity="0.7"/>' +
    '<path d="M10.6 21.4 L13 22.8" stroke-width="1" opacity="0.7"/>' +
    '<path d="M16.4 16.1 L17 18.7" stroke-width="1" opacity="0.7"/>' +
    '<path d="M25.2 19.6 C26.8 21.2 27.3 23 26.7 25.2" stroke-width="1.6"/>' +
    '<circle cx="26.7" cy="25.4" r="1.5" fill="#8f1814" stroke="none"/>' +
    '</g></svg>';

  function showLoading() {
    hideLoading();
    var step = document.getElementById('receipt-step');
    if (!step) return;
    var el = document.createElement('div');
    el.id = 'rc-loading';
    el.innerHTML =
      '<div class="rc-load-ring">' + SCORPION_SVG + '</div>' +
      '<p class="rc-load-title">Checking your receipt&hellip;</p>' +
      '<p class="rc-load-fine">This usually takes a few seconds.</p>';
    step.appendChild(el);
  }

  function hideLoading() {
    var el = document.getElementById('rc-loading');
    if (el && el.parentNode) el.parentNode.removeChild(el);
  }

  (function injectLoadingStyles() {
    var css =
      '#rc-loading{text-align:center;padding:26px 0 12px;}' +
      '.rc-load-ring{position:relative;width:122px;height:122px;margin:0 auto 18px;}' +
      '.rc-load-ring::before{content:"";position:absolute;inset:0;border-radius:50%;' +
        'border:3px solid #ececec;border-top-color:#8f1814;animation:rcspin 1s linear infinite;}' +
      '.rc-load-ring svg{position:absolute;inset:0;margin:auto;color:#1c1c1c;}' +
      '.rc-scorp-tail{transform-box:fill-box;transform-origin:100% 100%;animation:rctail 2.4s ease-in-out infinite;}' +
      '.rc-load-title{font-size:15px;font-weight:600;letter-spacing:.05em;color:#1c1c1c;margin:0 0 4px;}' +
      '.rc-load-fine{font-size:11px;color:#8a8a8a;letter-spacing:.08em;text-transform:uppercase;margin:0;}' +
      '@keyframes rcspin{to{transform:rotate(360deg)}}' +
      '@keyframes rctail{0%,100%{transform:rotate(0deg)}50%{transform:rotate(-7deg)}}' +
      '@media (prefers-reduced-motion:reduce){.rc-load-ring::before,.rc-scorp-tail{animation:none}}';
    var tag = document.createElement('style');
    tag.textContent = css;
    document.head.appendChild(tag);
  })();

  // ---- send + verdict poll (THE REWARD GATE) ---------------------------
  // The receipt decides the prize: POST -> 202 {receipt_id} -> poll the
  // public verdict endpoint until pass/fail. Only a PASS unlocks the patch;
  // fail/error let the shopper try another photo. (Dorian's rule 2026-08-17:
  // a negative answer does NOT unlock — no "reserved either way" promises.)
  //
  // TIMING (fixed 2026-08-18 after real verifications took >75s and the page
  // wrongly said "couldn't finish" while the server went on to VALIDATE and
  // email the claim link): fast polls for the first stretch, then a patient
  // slow-poll phase with honest copy, and only after 5 minutes a final
  // "we'll email you" message — never a dead "try again" that provokes
  // duplicate uploads of an already-winning receipt.
  var POLL_MS = 2500, POLL_SLOW_MS = 6000;
  var POLL_PATIENT_MS = 60000, POLL_TIMEOUT_MS = 300000;

  function showPatience() {
    var t = document.querySelector('#rc-loading .rc-load-title');
    var f = document.querySelector('#rc-loading .rc-load-fine');
    if (t) t.innerHTML = 'Still checking&hellip;';
    if (f) f.innerHTML = 'This one is taking a little longer than usual. ' +
      'Hang tight &mdash; if you close the page, your patch link still arrives by email.';
  }

  function send() {
    if (!_blob) return;
    var btn = document.getElementById('rc-send');
    if (btn) { btn.disabled = true; btn.textContent = 'Checking…'; }
    setStatus('', false);
    toggle('rc-preview', false);
    showLoading();

    var fd = new FormData();
    fd.append('receipt', _blob, 'receipt.jpg');
    if (_signup) {
      if (_signup.email)     fd.append('email', _signup.email);
      if (_signup.sku)       fd.append('sku', _signup.sku);
      if (_signup.sessionId) fd.append('sessionId', _signup.sessionId);
      if (_signup.visitorId) fd.append('visitorId', _signup.visitorId);
      if (_signup.region)    fd.append('region', _signup.region);
      fd.append('source', 'qr_free_patch');
    }

    if (ENDPOINT === '__console__') {
      console.log('[AGB QR receipt] send (dev stub):',
        { bytes: _blob.size, signup: _signup });
      window.setTimeout(function () {
        settle(window.__AGB_RECEIPT_STUB_VERDICT || 'pass');
      }, 2600);
      return;
    }
    fetch(ENDPOINT, { method: 'POST', body: fd })
      .then(function (r) { return r && r.ok ? r.json() : null; })
      .then(function (data) {
        if (data && data.status === 'receipt_already_used') { settle('receipt_used'); return; }
        if (!data || !data.receipt_id) { settle('error'); return; }
        poll(data.receipt_id, Date.now());
      })
      .catch(function () { settle('error'); });
  }

  function poll(id, t0) {
    fetch(ENDPOINT.replace(/\/+$/, '') + '/' + encodeURIComponent(id) + '/verdict')
      .then(function (r) { return r && r.ok ? r.json() : null; })
      .then(function (d) {
        var v = d && d.verdict;
        if (v === 'pass' || v === 'fail' || v === 'error' || v === 'already_claimed') {
          // A render bug must NEVER masquerade as "still checking": settle
          // errors end the poll with a visible fallback instead of being
          // swallowed by the retry .catch below (the 2026-08-18 lesson).
          try { settle(v, d); }
          catch (err) {
            try { console.error('[AGB QR] settle failed', err); } catch (e2) {}
            var done = document.getElementById('rc-done');
            if (done) {
              done.hidden = false;
              done.textContent = v === 'pass'
                ? 'Purchase verified — your free-patch link is on its way to your inbox.'
                : 'Verification finished — please refresh the page to see the result.';
            }
            hideLoading();
          }
          return;
        }
        schedule(id, t0);
      })
      .catch(function () { schedule(id, t0); });
  }

  function schedule(id, t0) {
    var elapsed = Date.now() - t0;
    if (elapsed > POLL_TIMEOUT_MS) { settle('slow'); return; }
    if (elapsed > POLL_PATIENT_MS) {
      showPatience();
      window.setTimeout(function () { poll(id, t0); }, POLL_SLOW_MS);
      return;
    }
    // The server's fast pre-check can pass a receipt ~2s in — poll eagerly
    // for the first seconds so that verdict is seen the moment it lands.
    window.setTimeout(function () { poll(id, t0); },
                      elapsed < 8000 ? 1000 : POLL_MS);
  }

  // HTML-escape for values interpolated into innerHTML. Each asset is its own
  // IIFE — qr-signup.js's esc() is NOT visible here; the pass-screen once
  // called it anyway and silently crashed the unlock render (2026-08-18).
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function settle(outcome, data) {
    hideLoading();
    var btn = document.getElementById('rc-send');
    if (btn) { btn.disabled = false; btn.textContent = 'Submit receipt'; }
    setStatus('', false);
    toggle('rc-preview', false);
    var d = document.getElementById('rc-done');
    if (!d) return;
    d.hidden = false;
    if (outcome === 'pass') {
      clearResume();
      if (window.AGBQrCrop) window.AGBQrCrop.reset();   // release the crop canvas
      var url = data && data.claim_url;
      var patch = (data && data.patch_title) || 'your free patch';
      if (url) {
        // Straight to the free-item page (Dorian 2026-08-18: no unlock
        // interstitial, no picture — just go). The link applies the one-use
        // code and opens the patch product. The line + link below only exist
        // as a fallback if the browser blocks the automatic navigation.
        d.innerHTML =
          '<p class="sub">Purchase verified — taking you to claim <strong>' + esc(patch) + '</strong>…</p>' +
          '<a class="ag-btn block" id="rc-claim" href="' + esc(url) + '">Claim your free patch &rarr;</a>';
        try { window.top.location.href = url; }
        catch (e) { window.location.href = url; }
      } else {
        d.innerHTML =
          '<div class="ok-badge">✓ Patch unlocked</div>' +
          '<p class="sub">Purchase verified. Watch your inbox for your free patch.</p>';
      }
      return;
    }
    if (outcome === 'already_claimed') {
      clearResume();
      d.innerHTML = '<p class="sub"><strong>It looks like you&rsquo;ve already registered this product.</strong> ' +
        'No additional reward will be issued for it — your patch link is in your inbox.</p>';
      return;
    }
    if (outcome === 'slow') {
      // Five minutes of pending — the check is still running server-side (or
      // held for a human look). NOT an error and NOT a retry prompt: a second
      // upload of the same receipt would only hit the dedup wall. The claim
      // email delivers the patch link whenever verification lands.
      clearResume();
      d.innerHTML =
        '<p class="sub"><strong>We&rsquo;re still checking your receipt.</strong> ' +
        'This one needs a closer look — you can close this page. ' +
        'If everything checks out, your free-patch link arrives by email.</p>';
      return;
    }
    if (outcome === 'receipt_used') {
      d.innerHTML = '<p class="sub"><strong>This receipt has already been used to claim a patch.</strong> ' +
        'Each receipt can unlock one patch only.</p>' +
        '<button type="button" class="ag-btn ag-btn--secondary block" id="rc-again">Try another receipt</button>';
      var againUsed = d.querySelector('#rc-again');
      if (againUsed) againUsed.addEventListener('click', function () {
        d.hidden = true; d.innerHTML = '';
        _blob = null;
        if (window.AGBQrCrop) window.AGBQrCrop.reset();
        toggle('rc-actions', true);
        if (!COARSE) toggle('rc-drop', true);
      });
      return;
    }
    d.innerHTML = (outcome === 'fail'
      ? '<p class="sub"><strong>We couldn’t find an Agilite purchase on this receipt.</strong> ' +
        'Try a clearer photo of the full receipt.</p>'
      : '<p class="sub"><strong>We couldn’t finish checking your receipt.</strong> Try again in a moment.</p>') +
      '<button type="button" class="ag-btn ag-btn--secondary block" id="rc-again">' +
      (outcome === 'fail' ? 'Try another photo' : 'Try again') + '</button>';
    var again = d.querySelector('#rc-again');
    if (again) again.addEventListener('click', function () {
      d.hidden = true; d.innerHTML = '';
      _blob = null;
      if (window.AGBQrCrop) window.AGBQrCrop.reset();
      toggle('rc-actions', true);
      if (!COARSE) toggle('rc-drop', true);
    });
  }

  // ---- small utils -------------------------------------------------------
  function bind(id, fn) { var el = document.getElementById(id); if (el) el.addEventListener('click', fn); }
  function toggle(id, showIt) { var el = document.getElementById(id); if (el) el.hidden = !showIt; }
  function setStatus(msg, isErr) {
    var el = document.getElementById('rc-status');
    if (!el) return;
    el.textContent = msg || '';
    el.hidden = !msg;
    el.classList.toggle('err', !!isErr);
  }

  // ---- wire up (elements exist in the page markup) -----------------------
  function init() {
    var cam = document.getElementById('rc-file-cam');
    var lib = document.getElementById('rc-file-lib');
    // Snap prefers the IN-PAGE camera (qr-livecam.js): no browser->camera-app
    // intent, which Motorola's camera app fails with a bogus "low memory"
    // toast. Denied/unavailable => the old capture input, unchanged.
    bind('rc-snap', function () {
      if (window.AGBQrLive && window.AGBQrLive.available()) {
        window.AGBQrLive.open(onFile).then(function (opened) {
          if (!opened && cam) cam.click();
        });
        return;
      }
      if (cam) cam.click();
    });
    bind('rc-upload', function () { if (lib) lib.click(); });
    if (cam) cam.addEventListener('change', function () { _lastSource = 'cam'; onFile(cam.files && cam.files[0]); });
    if (lib) lib.addEventListener('change', function () { _lastSource = 'lib'; onFile(lib.files && lib.files[0]); });
    bind('rc-send',   send);
    bind('rc-retake', function () {
      _blob = null;
      if (window.AGBQrCrop) window.AGBQrCrop.reset();
      toggle('rc-preview', false); toggle('rc-actions', true);
      if (!COARSE) toggle('rc-drop', true);
    });
    var drop = document.getElementById('rc-drop');
    if (drop) {
      ['dragover', 'dragenter'].forEach(function (t) {
        drop.addEventListener(t, function (e) { e.preventDefault(); drop.classList.add('over'); });
      });
      ['dragleave', 'drop'].forEach(function (t) {
        drop.addEventListener(t, function (e) { e.preventDefault(); drop.classList.remove('over'); });
      });
      drop.addEventListener('drop', function (e) {
        var f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
        _lastSource = 'drop';
        onFile(f);
      });
    }
  }
  function boot() { init(); if (enabled()) tryResume(); }
  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);

  window.AGBQrReceipt = { enabled: enabled, show: show };
})();
